/**
 * Lunar Dial — signature visual for Aramgah.
 *
 * A central plaque with:
 *   • Outer ring: conic-gradient moon-phase indicator. Phase angle is computed
 *     from a 29.53-day synodic period anchored to a known new-moon epoch.
 *   • Three inner concentric ribbons: REM (cool blue), Deep (indigo), Light
 *     (silver). Stroke length = pct of personal target (1.6/1.5/4.0h).
 *   • Center: avg-hours in italic Cormorant + tier name in Space Mono caps.
 *
 * Registers as <aramgah-stage-rings>. Name retained for compat with main.ts.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, formatHoursDecimal } from '../types.ts';
import type { SleepSummary } from '../types.ts';

const SYNODIC_DAYS = 29.530588853;
const KNOWN_NEW_MOON_MS = Date.UTC(2024, 0, 11, 11, 57, 0); // 2024-01-11 11:57 UTC

function moonPhaseTurn(now: Date = new Date()): number {
  const elapsedDays = (now.getTime() - KNOWN_NEW_MOON_MS) / 86_400_000;
  const phase = ((elapsedDays % SYNODIC_DAYS) + SYNODIC_DAYS) % SYNODIC_DAYS;
  return phase / SYNODIC_DAYS; // 0..1
}

function phaseName(turn: number): string {
  if (turn < 0.03 || turn > 0.97) return 'New';
  if (turn < 0.22) return 'Waxing crescent';
  if (turn < 0.28) return 'First quarter';
  if (turn < 0.47) return 'Waxing gibbous';
  if (turn < 0.53) return 'Full';
  if (turn < 0.72) return 'Waning gibbous';
  if (turn < 0.78) return 'Last quarter';
  return 'Waning crescent';
}

const RIBBON_META = [
  { key: 'rem',   label: 'REM',   color: 'oklch(72% 0.13 320)', target: 1.6, radius: 78, width: 8 },
  { key: 'deep',  label: 'Deep',  color: 'oklch(54% 0.14 268)', target: 1.5, radius: 62, width: 8 },
  { key: 'light', label: 'Light', color: 'oklch(82% 0.03 250)', target: 4.0, radius: 46, width: 8 },
] as const;

export class AramgahStageRings extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(s: SleepSummary): void {
    const tier = TIER_DISPLAY[s.tier];
    const avgLight = Math.max(0, s.avg_hours - s.avg_deep_hours - s.avg_rem_hours - 0.3);
    const stages: Record<string, number> = {
      rem: s.avg_rem_hours,
      deep: s.avg_deep_hours,
      light: avgLight,
    };

    const turn = moonPhaseTurn();
    const phaseAngle = turn * 360;
    const phaseLabel = phaseName(turn);

    const ribbons = RIBBON_META.map((m, i) => {
      const v = stages[m.key] ?? 0;
      const pct = Math.min(100, Math.max(4, (v / m.target) * 100));
      const r = m.radius;
      const circ = 2 * Math.PI * r;
      const dash = (pct / 100) * circ;
      const off = circ * 0.25;
      return `
<circle cx="120" cy="120" r="${r}" fill="none" stroke="${m.color}1f" stroke-width="${m.width}" />
<circle cx="120" cy="120" r="${r}" fill="none" stroke="${m.color}" stroke-width="${m.width}"
  stroke-dasharray="${dash.toFixed(2)} ${(circ - dash).toFixed(2)}"
  stroke-dashoffset="${off.toFixed(2)}"
  stroke-linecap="round"
  style="animation: ring-draw 1100ms var(--ease-suite) both; animation-delay: ${i * 110 + 200}ms;"
/>`;
    }).join('');

    const legendRows = RIBBON_META.map((m) => {
      const v = stages[m.key] ?? 0;
      return `
<div class="leg">
  <span class="dot" style="background:${m.color}"></span>
  <span class="lk">${m.label}</span>
  <span class="lv">${v.toFixed(1)}h</span>
</div>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
  :host {
    display: block;
    container-type: inline-size;
    border: 1px solid var(--rule);
    border-radius: 12px;
    padding: 26px 22px 30px;
    background:
      radial-gradient(ellipse 70% 50% at 50% 0%, oklch(22% 0.06 270 / 0.6), transparent 60%),
      var(--paper-up);
    position: relative;
    overflow: hidden;
  }
  :host::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 24% 14%, oklch(82% 0.04 250 / 0.04), transparent 32%),
      radial-gradient(circle at 78% 92%, oklch(82% 0.04 320 / 0.04), transparent 32%);
    pointer-events: none;
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 18px;
    position: relative;
    z-index: 1;
  }
  .head .eyebrow {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .head .phase {
    font-family: var(--display);
    font-style: italic;
    font-size: 14px;
    color: var(--ink-dim);
  }
  .stage {
    display: grid;
    grid-template-columns: minmax(220px, 280px) 1fr;
    gap: 24px;
    align-items: center;
    position: relative;
    z-index: 1;
  }
  @container (max-width: 460px) {
    .stage { grid-template-columns: 1fr; gap: 18px; }
  }
  .dial {
    position: relative;
    width: 100%;
    max-width: 280px;
    aspect-ratio: 1 / 1;
    margin-inline: auto;
  }
  .dial-phase {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background:
      conic-gradient(
        from -90deg,
        oklch(91% 0.04 80 / 0.62) 0deg,
        oklch(91% 0.04 80 / 0.62) ${phaseAngle.toFixed(2)}deg,
        oklch(7% 0.02 268 / 0.85) ${phaseAngle.toFixed(2)}deg,
        oklch(7% 0.02 268 / 0.85) 360deg
      );
    -webkit-mask: radial-gradient(circle, transparent 92px, black 93px, black 110px, transparent 111px);
            mask: radial-gradient(circle, transparent 92px, black 93px, black 110px, transparent 111px);
    filter: blur(0.4px);
    opacity: 0.85;
    --moon-phase: ${phaseAngle.toFixed(2)}deg;
    animation: phase-breathe 9s ease-in-out infinite alternate;
  }
  .dial-svg { position: absolute; inset: 0; width: 100%; height: 100%; }
  .dial-center {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    text-align: center;
    pointer-events: none;
  }
  .dial-hours {
    font-family: var(--display);
    font-style: italic;
    font-weight: 600;
    font-size: clamp(36px, 11cqw, 56px);
    line-height: 0.95;
    color: var(--moon-cream);
    letter-spacing: -0.02em;
    font-feature-settings: 'tnum' 1, 'ss01' 1, 'cv11' 1;
    text-shadow: 0 0 18px oklch(91% 0.04 80 / 0.4);
  }
  .dial-tier {
    margin-top: 8px;
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .legend { display: grid; gap: 12px; }
  .leg {
    display: grid;
    grid-template-columns: 14px 1fr auto;
    align-items: baseline;
    gap: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed var(--rule);
  }
  .leg:last-child { border-bottom: none; }
  .dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    transform: translateY(2px);
    box-shadow: 0 0 8px currentColor;
  }
  .lk {
    font-family: var(--display);
    font-style: italic;
    font-size: 18px;
    font-weight: 500;
    color: var(--ink);
  }
  .lv {
    font-family: var(--display);
    font-size: 22px;
    font-weight: 600;
    color: var(--moon-cream);
    font-feature-settings: 'tnum' 1, 'ss01' 1;
    letter-spacing: -0.01em;
  }
  .meta-strip {
    margin-top: 22px;
    padding-top: 14px;
    border-top: 1px solid var(--rule);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--ink-faint);
    position: relative;
    z-index: 1;
  }
  .meta-strip em {
    font-style: italic;
    font-family: var(--display);
    text-transform: none;
    letter-spacing: 0.01em;
    font-size: 13px;
    color: var(--ink-dim);
  }
  @keyframes ring-draw {
    from { stroke-dasharray: 0 9999; }
  }
  @keyframes phase-breathe {
    from { filter: blur(0.4px) brightness(1.0); }
    to   { filter: blur(0.6px) brightness(1.08); }
  }
  @media (prefers-reduced-motion: reduce) {
    .dial-phase { animation: none; }
    circle { animation: none !important; }
  }
</style>
<div class="head">
  <div class="eyebrow">Lunar dial · 30-night avg</div>
  <div class="phase">${phaseLabel} moon</div>
</div>
<div class="stage">
  <div class="dial" role="img" aria-label="Sleep stage dial. ${tier.name} tier. ${formatHoursDecimal(s.avg_hours)} per night.">
    <div class="dial-phase"></div>
    <svg class="dial-svg" viewBox="0 0 240 240" aria-hidden="true">${ribbons}</svg>
    <div class="dial-center">
      <div>
        <div class="dial-hours">${formatHoursDecimal(s.avg_hours)}</div>
        <div class="dial-tier">${tier.name}</div>
      </div>
    </div>
  </div>
  <div class="legend">${legendRows}</div>
</div>
<div class="meta-strip">
  <span>Stage targets · 1.6 / 1.5 / 4.0h</span>
  <em>"${tier.tagline}"</em>
</div>
`;
  }
}

customElements.define('aramgah-stage-rings', AramgahStageRings);
